import { FC, useEffect } from 'react'
import PageContentWrapper from '@/components/organisms/pageContentWrapper'
import { Box, Flex } from '@/components/atoms/Grid'
import { FilterType, ListingProducts, Sdk } from '@/network/graphql.g'
import { Body3, H2 } from '@/components/atoms/typography'
import useTranslation from 'next-translate/useTranslation'
import { useActiveFilterDispatch } from '@/providers/filterProvider/filtersProvider'
import {
  ProductListingFetcher,
  ProductListingFetcherResponse
} from '@/components/organisms/productListing/productListingFetcher'
import { convertAPIProductToProduct } from '@/types/bridgeProduct'
import { Config } from '@/core/config'
import {
  withFilterProvider,
  WithFilterProviderProps
} from '@/HOC/withFilterProvider'
import { Routes } from '@/core/routes'
import CategoryPathHeader from '@/components/molecules/categoryPathHeader'
import { ListItemSeparator } from '@/components/atoms/separator'
import styled from 'styled-components'
import { validFor } from '@/theme/validFor'
import useGraphQl from 'hooks/useGraphQl'
import { requestThrow401 } from '@/utils/graphQl'
import ProductListingTitleCountInfo from '@/components/molecules/productListingTitleCountInfo'
import SearchProductsContainer from '@/components/organisms/searchProductsContainer'
import { withBaseServerSideProps } from '@/HOC/withBaseServerSideProps'
import { useMhub } from '@/providers/mhubProvider'
import useProductListing from 'hooks/useProductListing'
import { getProductListingData } from '@/components/organisms/productListing/productListingRedesign'
import { getMainData } from '@/utils/mainDataHelper'
import { SentryError } from '@/core/sentryError'
import { useSpeedCurve } from '@/core/speedCurve/useSpeedCurve'
import { useRouter } from 'next/router'
import { useNavigationSource } from '@/providers/navigationSourceProvider'

type SearchProps = {
  query: string
  listingProducts?: ListingProducts
  filterId: string
  requestId: string
  isPreload: boolean
} & WithFilterProviderProps

const Search: FC<SearchProps> = ({
  query,
  listingProducts,
  filterId,
  requestId,
  isPreload,
  ...rest
}) => {
  const { t } = useTranslation('common')
  const { setFilters } = useActiveFilterDispatch()
  const { requestNotifyError } = useGraphQl()
  const router = useRouter()

  const fetcher: ProductListingFetcher = async (
    page,
    filterInput,
    refresh,
    initialLoad = false
  ): Promise<ProductListingFetcherResponse> => {
    const result = await requestNotifyError(
      (sdk: Sdk) => ({
        method: sdk.fullTextSearchNextProducts,
        variables: {
          page: page,
          limit: Config.perPageLuigi,
          filter: filterInput,
          withFilterAndProductsCount: refresh
        }
      }),
      true
    )

    if (!result.success) {
      throw (
        result.error ??
        new SentryError('Search product listing', result.response)
      )
    }

    const { products, filter, totalPagesCount } =
      result.response?.fulltextProducts ?? {}
    if (filter) {
      setFilters(filter)
    }

    !initialLoad &&
      pushSimpleSearchDetailPage(
        filterInput?.find((item) => item.filterType === FilterType.Search)
          ?.filterIDs?.[0] ?? query,
        result.response?.fulltextProducts?.totalProductsCount,
        page
      )

    const productList =
      products?.map((product) => convertAPIProductToProduct(product)) ?? []

    if (!initialLoad && !router.query.a) {
      window.scrollTo(0, 0)
    }

    return {
      products: productList,
      totalPagesCount
    }
  }

  const { pushSearchDetailPage, pushSimpleSearchDetailPage } = useMhub()
  const { pushSearchWhisperer: pushSearchWhisperer } = useMhub()

  useEffect(() => {
    pushSearchDetailPage(query, listingProducts?.totalProductsCount)
    pushSearchWhisperer(query, listingProducts?.totalProductsCount ?? 0)
  }, [query, listingProducts?.totalProductsCount])

  const { data } = useProductListing({
    totalPagesCount: listingProducts?.totalPagesCount,
    totalProductsCount: listingProducts?.totalProductsCount,
    id: query,
    products: listingProducts?.products,
    filterId,
    requestId,
    isPreload,
    fetcher
  })

  useSpeedCurve({
    label: 'Search'
  })

  const { setNavigationSource } = useNavigationSource()

  useEffect(() => {
    setNavigationSource({
      from: 'search',
      name: t('Search.breadCrumbsTitle') + ' ' + query,
      slug: '?search=' + query
    })
  }, [])

  return (
    <PageContentWrapper>
      <Box key={query} {...rest}>
        <CategoryPathHeader
          paths={[
            {
              title: t('Search.breadCrumbsTitle'),
              url: `${Routes.search}?search=${query}`
            }
          ]}
        />
        {(listingProducts?.products?.length ?? 0) === 0 ? (
          <Box>
            <H2 mb={20}>
              {t('Search.noResultsTitle', { searchQuery: query })}
            </H2>
            <ListItemSeparator />
            <StyledNoResultsDesc>
              {t('Search.noResultsDesc')}
            </StyledNoResultsDesc>
          </Box>
        ) : (
          <>
            <Flex alignItems="center">
              <ProductListingTitleCountInfo
                title={t('Search.title', {
                  query: query
                })}
              />
            </Flex>
            <SearchProductsContainer
              productListingFetcher={fetcher}
              data={data}
              showFilteredBy={true}
              list="list.searchresults.search"
            />
          </>
        )}
      </Box>
    </PageContentWrapper>
  )
}

export default withFilterProvider(Search)

export const getServerSideProps = withBaseServerSideProps(
  async ({
    context,
    sdk,
    withMainNavigation,
    withStore
  }): Promise<GetServerSideProps<SearchProps>> => {
    const productListingData = getProductListingData(context)
    if ('redirect' in productListingData) {
      return {
        redirect: productListingData.redirect
      }
    }
    const { page, filterInput, filterId, requestId, isPreload } =
      productListingData

    const query = context.query?.search?.toString() ?? ''

    const { success, response } = await requestThrow401({
      method: sdk.fullTextSearchProducts,
      variables: {
        filter: filterInput,
        limit: Config.perPageLuigi,
        page,
        withMainNavigation,
        withStore,
        searchTerm: query
      },
      mainResponse: (response) => response?.fulltextProducts
    })
    if (!success) {
      return {
        notFound: true
      }
    }

    if (response?.seoTags) {
      response.seoTags.image =
        response?.fulltextProducts?.products?.find((item) => item)?.mainImage
          ?.url ?? null
    }

    return {
      props: {
        mainData: getMainData(response),
        seoTags: response?.seoTags,
        query: query,
        listingProducts: response?.fulltextProducts,
        filter: response?.fulltextProducts?.filter ?? [],
        filterId,
        requestId,
        isPreload,
        disableDaktela: true,
        page: page
      }
    }
  }
)

const StyledNoResultsDesc = styled(Body3)`
  text-align: start;
  opacity: 60%;
  margin-bottom: 200px;
  margin-top: 60px;
  ${validFor.tablet`    
    margin-bottom: 150px;
    margin-top: 40px;
  `}
  ${validFor.mobile`
    text-align: center; 
    margin: 20px 50px 100px;
  `}
`
